import { withAlpha } from "@/lib/color";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crosshair, Trophy, Shield } from "lucide-react";
import { api } from "@/lib/api";

const BOARDS = [
    {
        key: "scorers",
        title: "CANNONIERI",
        subtitle: "Gol totali · campionati + coppe",
        accent: "rgb(var(--tc-ff7a1a))",
        icon: Crosshair,
        metric: (p) => (p.goals ?? null),
        unit: "gol",
    },
    {
        key: "mvp",
        title: "PALLONE D'ORO",
        subtitle: "Migliore in campo · totale stagione",
        accent: "rgb(var(--tc-e8ff00))",
        icon: Trophy,
        metric: (p) => (p.mvp_count ?? null),
        unit: "MVP",
    },
    {
        key: "goalkeepers",
        title: "MIGLIOR PORTIERE",
        subtitle: "Trofeo Yashin · media voto",
        accent: "rgb(var(--tc-2bb6ff))",
        icon: Shield,
        metric: (p) => (p.avg_rating ?? null),
        unit: "voto medio",
    },
];

export default function GlobalLeaderboards({ limit = 8 }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/leaderboards/global?limit=${limit}`);
                setData(data);
            } catch {
                /* ignore */
            } finally {
                setLoading(false);
            }
        })();
    }, [limit]);

    if (loading) {
        return (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="global-leaderboards-loading">
                {BOARDS.map((b) => (
                    <div key={b.key} className="op-card h-80 animate-pulse bg-t0d0f12" />
                ))}
            </div>
        );
    }

    if (!data) return null;
    const isPreview = data.is_preview;

    return (
        <div data-testid="global-leaderboards">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {BOARDS.map((b) => (
                    <BoardCard
                        key={b.key}
                        board={b}
                        players={data[b.key] || []}
                        isPreview={isPreview}
                    />
                ))}
            </div>
            {isPreview && (
                <p className="op-mono text-[11px] text-t5c6370 mt-4" data-testid="global-leaderboards-preview-note">
                    Anteprima ordinata per VG · si aggiorna automaticamente con i tabellini delle partite.
                </p>
            )}
        </div>
    );
}

function BoardCard({ board, players, isPreview }) {
    const Icon = board.icon;
    return (
        <div className="op-card p-0 overflow-hidden" data-testid={`board-${board.key}`}>
            <div
                className="px-5 py-4 border-b border-t22252a flex items-center gap-3"
                style={{ background: `linear-gradient(90deg, ${withAlpha(board.accent, "14")}, transparent)` }}
            >
                <div
                    className="w-9 h-9 flex items-center justify-center shrink-0 border"
                    style={{ borderColor: `${withAlpha(board.accent, "55")}`, color: board.accent }}
                >
                    <Icon size={18} strokeWidth={2.2} />
                </div>
                <div className="min-w-0">
                    <div className="op-heading text-lg leading-none" style={{ color: board.accent }}>
                        {board.title}
                    </div>
                    <div className="op-mono text-[10px] text-t8f95a3 mt-1 truncate">{board.subtitle}</div>
                </div>
            </div>

            <ul className="divide-y divide-t16181d">
                {players.length === 0 && (
                    <li className="px-5 py-6 op-mono text-xs text-t5c6370">Nessun dato disponibile.</li>
                )}
                {players.map((p) => {
                    const value = isPreview ? p.vg : board.metric(p);
                    return (
                        <li
                            key={`${board.key}-${p.pes_id}-${p.rank}`}
                            className="px-4 py-2.5 flex items-center gap-3 hover:bg-t101216 transition-colors"
                            data-testid={`board-${board.key}-row-${p.rank}`}
                        >
                            <span
                                className="op-mono text-sm w-5 text-center shrink-0"
                                style={{ color: p.rank <= 3 ? board.accent : "rgb(var(--tc-5c6370))" }}
                            >
                                {p.rank}
                            </span>
                            <Avatar src={p.image_url} accent={board.accent} />
                            <Link
                                to={p.pes_id ? `/giocatori/${p.pes_id}` : "#"}
                                className="min-w-0 flex-1 group"
                            >
                                <div className="text-tffffff text-sm font-semibold truncate group-hover:text-t2bb6ff transition-colors">
                                    {p.name || "—"}
                                </div>
                                <div className="flex items-center gap-1.5 mt-0.5">
                                    {p.team_logo && (
                                        <img src={p.team_logo} alt="" className="w-3.5 h-3.5 object-contain" />
                                    )}
                                    <span className="op-mono text-[10px] text-t8f95a3 truncate">
                                        {p.team_name || "—"}
                                    </span>
                                </div>
                            </Link>
                            <div className="text-right shrink-0">
                                <div className="op-mono text-base font-bold" style={{ color: board.accent }}>
                                    {value ?? "—"}
                                </div>
                                <div className="op-mono text-[9px] text-t5c6370 uppercase">
                                    {isPreview ? "VG" : board.unit}
                                </div>
                            </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

function Avatar({ src, accent }) {
    return (
        <div
            className="w-8 h-8 rounded-full overflow-hidden shrink-0 bg-t0d0f12 border flex items-center justify-center"
            style={{ borderColor: `${withAlpha(accent, "33")}` }}
        >
            {src ? (
                <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" />
            ) : (
                <span className="op-mono text-[10px] text-t5c6370">—</span>
            )}
        </div>
    );
}
